import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Account } from "models/Account";
import { Person } from "models/Person";
import { Tenant } from "models/Tenant";
import { Email } from "models/Email";
import { Invoice } from "models/Invoice";
import * as api from "services/Api";
import { GET_EMAIL_THREAD, SEND_EMAIL } from "store/actions/Emails";
import { b64toBlob } from "common/base64toblob";
import {
    buildContacts, ComposeContext, ComposerAttachment, ComposerDraft, ComposerRecipient,
    createEmailPayload, draftProblem, emptyDraft, initializeDraft, optionalId, uniqueRecipients,
} from "./composerAdapters";

let attachmentSequence = 0;
const attachmentId = () => `compose-${++attachmentSequence}`;

export const useEmailComposer = (context: ComposeContext) => {
    const dispatch = useDispatch();
    const [draft, setDraft] = useState<ComposerDraft>(emptyDraft);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [persons, setPersons] = useState<Person[]>([]);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [reply, setReply] = useState<Email | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<unknown>(null);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [sendError, setSendError] = useState<unknown>(null);
    const [sending, setSending] = useState(false);
    const sendingRef = useRef(false);
    const active = useRef(true);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoicesError, setInvoicesError] = useState<unknown>(null);
    const invoicesLoaded = useRef(false);
    const invoicesRequest = useRef<Promise<void> | null>(null);

    const { replyId, accountId, personId } = context;
    useEffect(() => {
        let cancelled = false;
        active.current = true;
        setLoading(true);
        setLoadError(null);
        Promise.all([api.getAccounts(), api.getPersons(), api.getTenants(), replyId ? api.getEmailThread(replyId) : Promise.resolve([] as Email[])])
            .then(([nextAccounts, nextPersons, nextTenants, thread]) => {
                if (cancelled) return;
                const nextReply = thread.find(value => value.id === replyId) ?? null;
                if ((replyId && !nextReply) || (!replyId && accountId && !nextAccounts.some(value => value.id === accountId))
                    || (!replyId && personId && !nextPersons.some(value => value.id === personId))) {
                    setLoadError(new Error("compose context not found"));
                    return;
                }
                setAccounts(nextAccounts);
                setPersons(nextPersons);
                setTenants(nextTenants);
                setReply(nextReply);
                setDraft(initializeDraft({ replyId, accountId, personId }, nextAccounts, nextPersons, nextTenants, nextReply));
                if (thread.length) dispatch({ type: GET_EMAIL_THREAD.SUCCESS, payload: thread });
            }).catch(reason => { if (!cancelled) setLoadError(reason); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; active.current = false; };
    }, [replyId, accountId, personId, dispatch, loadAttempt]);

    const contacts = useMemo(() => buildContacts(accounts, persons), [accounts, persons]);
    const patch = useCallback((values: Partial<ComposerDraft>) => {
        setSendError(null);
        setDraft(current => ({ ...current, ...values }));
    }, []);

    const selectTo = (value: ComposerRecipient | null) => {
        setSendError(null);
        setDraft(current => {
            const suggested = !current.accountId && !current.replyToId ? accounts.find(account => account.id === value?.accountId) : undefined;
            return {
                ...current, to: value, toInput: "", cc: uniqueRecipients(current.cc, value),
                accountId: suggested?.id ?? current.accountId,
                tenantId: optionalId(suggested?.tenant) ?? current.tenantId,
            };
        });
    };

    const selectCc = (values: ComposerRecipient[]) => {
        setSendError(null);
        setDraft(current => ({ ...current, cc: uniqueRecipients(values, current.to), ccInput: "" }));
    };

    const selectAccount = (id: string | null) => {
        const account = accounts.find(value => value.id === id);
        patch({ accountId: optionalId(id), ...(account ? { tenantId: optionalId(account.tenant) } : {}) });
    };

    const addFiles = (files: File[]) => {
        setSendError(null);
        const added: ComposerAttachment[] = files.map(file => ({ id: attachmentId(), name: file.name, size: file.size, blob: file, status: "ready" }));
        setDraft(current => ({ ...current, attachments: [...current.attachments, ...added] }));
    };

    const removeAttachment = (id: string) => {
        setSendError(null);
        setDraft(current => {
            const removed = current.attachments.find(value => value.id === id);
            const remaining = current.attachments.filter(value => value.id !== id);
            const invoice = [...remaining].reverse().find(value => value.invoiceId && value.status === "ready");
            const reference = removed?.invoiceId && current.reference === `invoice_${removed.invoiceId}`
                ? invoice ? `invoice_${invoice.invoiceId}` : reply?.reference ?? "" : current.reference;
            return { ...current, attachments: remaining, reference };
        });
    };

    const loadInvoices = async () => {
        if (invoicesLoaded.current) return;
        if (invoicesRequest.current) return invoicesRequest.current;
        setInvoicesLoading(true);
        setInvoicesError(null);
        invoicesRequest.current = api.getInvoices().then(values => {
            if (!active.current) return;
            setInvoices(values);
            invoicesLoaded.current = true;
        }).catch(reason => { if (active.current) setInvoicesError(reason); })
            .finally(() => {
                invoicesRequest.current = null;
                if (active.current) setInvoicesLoading(false);
            });
        return invoicesRequest.current;
    };

    const addInvoice = async (invoice: Invoice) => {
        if (draft.attachments.some(value => value.invoiceId === invoice.id)) return;
        const id = attachmentId();
        const item: ComposerAttachment = { id, name: `${invoice.GetDisplayName()}.pdf`, size: 0, blob: null, status: "reading", invoiceId: invoice.id };
        setDraft(current => current.attachments.some(value => value.invoiceId === invoice.id) ? current : { ...current, attachments: [...current.attachments, item] });
        try {
            const pdf = await api.getInvoiceExport(invoice.id);
            const blob = b64toBlob(pdf, "application/pdf");
            if (!active.current) return;
            setDraft(current => !current.attachments.some(value => value.id === id) ? current : {
                ...current, reference: `invoice_${invoice.id}`,
                attachments: current.attachments.map(value => value.id === id ? { ...value, size: blob.size, blob, status: "ready" } : value),
            });
        } catch {
            if (active.current) setDraft(current => ({ ...current, attachments: current.attachments.map(value => value.id === id ? { ...value, status: "error" } : value) }));
        }
    };

    const submit = async (): Promise<Email | null> => {
        if (sendingRef.current || draftProblem(draft)) return null;
        sendingRef.current = true;
        setSending(true);
        setSendError(null);
        dispatch({ type: SEND_EMAIL.REQUEST });
        try {
            const payload = await createEmailPayload(draft);
            const sent = await api.sendEmail(payload);
            dispatch({ type: SEND_EMAIL.SUCCESS, payload: sent });
            return sent;
        } catch (reason) {
            dispatch({ type: SEND_EMAIL.FAILURE, payload: {} });
            if (active.current) setSendError(reason);
            return null;
        } finally {
            sendingRef.current = false;
            if (active.current) setSending(false);
        }
    };

    return {
        draft, accounts, tenants, reply, contacts, loading, loadError, sendError, sending,
        patch, selectTo, selectCc, selectAccount, addFiles, removeAttachment, submit,
        invoices, invoicesLoading, invoicesError, loadInvoices, addInvoice,
        retryLoading: () => setLoadAttempt(value => value + 1),
    };
};

export type EmailComposer = ReturnType<typeof useEmailComposer>;
